import * as React from "react";
import {
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Textarea,
} from "@eduviaos/ui-kit";
import type {
  ApplyResult,
  CapabilitySuggestion,
  FieldSuggestion,
  FunnelSuggestion,
  RequirementSuggestion,
  StageSuggestion,
  TargetKind,
} from "@eduviaos/shared";
import { MAX_PROMPT_LENGTH, MIN_PROMPT_LENGTH } from "@eduviaos/shared";
import { AlertTriangle, Sparkles } from "lucide-react";

import { useSuggestionSelection } from "../hooks/useSuggestionSelection.js";
import type { SelectableNode } from "../lib/collectNodes.js";
import { AIFormFieldDiff } from "./AIFormFieldDiff.js";
import { AIFunnelDiff } from "./AIFunnelDiff.js";
import { AIRequirementCard } from "./AIRequirementCard.js";
import { AIStageDiff } from "./AIStageDiff.js";

/**
 * AI suggestion drawer (Phase F-finale-4 Slice 5).
 *
 * Controlled, presentational shell — it holds no network state. The host app
 * (Slice 6) owns the TanStack mutation/query and feeds the result in via the
 * {@link DrawerState} discriminated union; the drawer renders the matching UI
 * and routes user intent back through `onGenerate` / `onApply` / `onReject` /
 * `onRetry`. Keeping the package free of react-query lets both SA and IA
 * portals reuse it with their own client singletons.
 */

/** Classified provider/route failure. Mirrors the BE error matrix. */
export type DrawerErrorVariant =
  /** Unexpected/other failure. */
  | "generic"
  /** `AIParseError` (502) — model returned malformed JSON twice. */
  | "parse"
  /** Daily cost cap reached (429 + `Retry-After`). */
  | "cost"
  /** Both providers circuit-broken (503 `AIUnavailableError`). */
  | "unavailable";

export type DrawerState =
  | { status: "idle" }
  | { status: "loading" }
  | {
      status: "error";
      variant: DrawerErrorVariant;
      message: string;
      /** For `cost` — seconds until the cap resets. */
      retryAfterSeconds?: number;
      /** For `parse` — the offending (PII-stripped) JSON fragment. */
      fragment?: string;
    }
  | { status: "success"; suggestion: CapabilitySuggestion }
  | { status: "applying"; suggestion: CapabilitySuggestion }
  | { status: "applied"; result: ApplyResult };

export interface AIAssistDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetKind: TargetKind;
  state: DrawerState;
  onGenerate: (prompt: string) => void;
  onApply: (acceptedNodeIds: string[]) => void;
  onReject: () => void;
  onRetry?: () => void;
}

const TARGET_LABEL: Record<TargetKind, string> = {
  funnel: "funnel",
  stage: "stage",
  requirement: "requirement",
  field: "form field",
};

const PROMPT_PLACEHOLDER: Record<TargetKind, string> = {
  funnel: "e.g. Build a class 6 admissions funnel with an interview and a written test.",
  stage: "e.g. Add an interview stage that requires a counsellor sign-off.",
  requirement: "e.g. Require a passing score on the entrance assessment.",
  field: "e.g. Add a file upload for the applicant's Aadhaar card.",
};

function formatWait(seconds: number): string {
  if (seconds >= 60) return `${Math.round(seconds / 60)} min`;
  return `${seconds} sec`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Diff dispatcher — picks the right renderer for the suggestion's target_kind
// ─────────────────────────────────────────────────────────────────────────────

function fallbackNode(nodeId: string): SelectableNode {
  return { node_id: nodeId, isValid: true };
}

interface SuggestionReviewProps {
  suggestion: CapabilitySuggestion;
  onApply: (acceptedNodeIds: string[]) => void;
  onReject: () => void;
  applying: boolean;
}

function SuggestionReview({ suggestion, onApply, onReject, applying }: SuggestionReviewProps) {
  const selection = useSuggestionSelection(suggestion);
  const acceptedCount = selection.acceptedNodeIds.length;

  const payload = suggestion.parsed_payload;
  let body: React.ReactNode;
  switch (suggestion.target_kind) {
    case "funnel":
      body = <AIFunnelDiff funnel={payload as FunnelSuggestion} selection={selection} />;
      break;
    case "stage":
      body = <AIStageDiff stage={payload as StageSuggestion} selection={selection} />;
      break;
    case "requirement": {
      const req = payload as RequirementSuggestion;
      body = (
        <AIRequirementCard
          requirement={req}
          node={selection.nodeById.get(req.node_id) ?? fallbackNode(req.node_id)}
          checked={selection.isSelected(req.node_id)}
          onToggle={() => selection.toggle(req.node_id)}
        />
      );
      break;
    }
    case "field": {
      const field = payload as FieldSuggestion;
      body = (
        <AIFormFieldDiff
          field={field}
          node={selection.nodeById.get(field.node_id) ?? fallbackNode(field.node_id)}
          checked={selection.isSelected(field.node_id)}
          onToggle={() => selection.toggle(field.node_id)}
        />
      );
      break;
    }
    default:
      body = null;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-muted-foreground text-xs">
          {acceptedCount} of {selection.selectableCount} selected
        </p>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={selection.selectAll}>
            Select all
          </Button>
          <Button variant="ghost" size="sm" onClick={selection.deselectAll}>
            Clear
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">{body}</div>

      <div className="border-border flex items-center justify-end gap-2 border-t pt-4">
        <Button variant="outline" onClick={onReject} disabled={applying}>
          Reject all
        </Button>
        <Button
          onClick={() => onApply(selection.acceptedNodeIds)}
          disabled={applying || acceptedCount === 0}
        >
          {applying ? "Applying…" : `Apply ${acceptedCount} selected`}
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Drawer shell
// ─────────────────────────────────────────────────────────────────────────────

export function AIAssistDrawer({
  open,
  onOpenChange,
  targetKind,
  state,
  onGenerate,
  onApply,
  onReject,
  onRetry,
}: AIAssistDrawerProps) {
  const [prompt, setPrompt] = React.useState("");
  const trimmed = prompt.trim();
  const promptValid = trimmed.length >= MIN_PROMPT_LENGTH && trimmed.length <= MAX_PROMPT_LENGTH;

  const showPromptInput = state.status === "idle" || state.status === "error";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-4 sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="size-4" aria-hidden />
            AI assist — {TARGET_LABEL[targetKind]}
          </SheetTitle>
          {/* Radix auto-wires SheetContent's aria-describedby to this
              Dialog.Description via context — do NOT set the id/aria-describedby
              manually, which overrides the context and trips Radix's
              missing-description warning even though the DOM looks correct. */}
          <SheetDescription>
            Describe what you want and review every suggested change before applying. Nothing is
            saved until you accept.
          </SheetDescription>
        </SheetHeader>

        {showPromptInput ? (
          <div className="flex flex-col gap-2">
            <Textarea
              name="ai-assist-prompt"
              aria-label={`Describe the ${TARGET_LABEL[targetKind]} to generate`}
              placeholder={PROMPT_PLACEHOLDER[targetKind]}
              value={prompt}
              maxLength={MAX_PROMPT_LENGTH}
              rows={4}
              onChange={(e) => setPrompt(e.target.value)}
            />
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-[11px]">
                {trimmed.length}/{MAX_PROMPT_LENGTH}
              </span>
              <Button onClick={() => onGenerate(trimmed)} disabled={!promptValid}>
                Generate
              </Button>
            </div>
          </div>
        ) : null}

        {state.status === "loading" ? (
          <div className="flex flex-col gap-2" aria-live="polite">
            <p className="text-muted-foreground text-sm">Generating suggestions…</p>
            {[0, 1, 2].map((i) => (
              <div key={i} className="bg-muted h-12 animate-pulse rounded-md" />
            ))}
          </div>
        ) : null}

        {state.status === "error" ? (
          <div
            role="alert"
            className="border-destructive/40 bg-destructive/5 flex flex-col gap-3 rounded-md border p-4"
          >
            <div className="text-destructive flex items-center gap-2 text-sm font-medium">
              <AlertTriangle className="size-4" aria-hidden />
              {state.message}
            </div>
            {state.variant === "cost" && state.retryAfterSeconds ? (
              <p className="text-muted-foreground text-xs">
                Try again in about {formatWait(state.retryAfterSeconds)}.
              </p>
            ) : null}
            {state.variant === "parse" && state.fragment ? (
              <pre className="bg-muted text-muted-foreground max-h-32 overflow-auto rounded p-2 text-[11px]">
                {state.fragment}
              </pre>
            ) : null}
            {onRetry && state.variant !== "cost" ? (
              <div>
                <Button variant="outline" size="sm" onClick={onRetry}>
                  Try again
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}

        {state.status === "success" || state.status === "applying" ? (
          <SuggestionReview
            suggestion={state.suggestion}
            onApply={onApply}
            onReject={onReject}
            applying={state.status === "applying"}
          />
        ) : null}

        {state.status === "applied" ? (
          <div className="flex flex-col gap-2 rounded-md border p-4" role="status">
            <p className="text-sm font-medium">Changes applied to your draft</p>
            <p className="text-muted-foreground text-sm">
              {state.result.applied_node_ids.length} applied
              {state.result.skipped_node_ids.length > 0
                ? ` · ${state.result.skipped_node_ids.length} skipped`
                : ""}
            </p>
            {state.result.skipped_node_ids.length > 0 ? (
              <ul className="text-muted-foreground list-disc space-y-0.5 pl-5 text-xs">
                {state.result.skipped_node_ids.map((s) => (
                  <li key={s.node_id}>
                    <code>{s.node_id}</code> — {s.reason}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
